import React from 'react'
import styles from './ProgressBanner.module.css'
import { CurrentStepProps } from './types'
import { ProgressBannerHeader } from './ProgressBannerHeader'
import { JOB_SEEKER_PROGRESS_DOT_IMAGES } from './constants'

const TOTAL_DOTS = 3

const DOT_LABELS = ['基本情報', '経歴', '設定・確認']

const ACTIVE_ICONS = [
    JOB_SEEKER_PROGRESS_DOT_IMAGES.ACTIVE_DOT_1,
    JOB_SEEKER_PROGRESS_DOT_IMAGES.ACTIVE_DOT_2,
    JOB_SEEKER_PROGRESS_DOT_IMAGES.ACTIVE_DOT_3,
]

const PENDING_ICONS = [
    JOB_SEEKER_PROGRESS_DOT_IMAGES.PENDING_DOT_1,
    JOB_SEEKER_PROGRESS_DOT_IMAGES.PENDING_DOT_2,
    JOB_SEEKER_PROGRESS_DOT_IMAGES.PENDING_DOT_3,
]

const SEGMENT_COLORS = {
    PENDING: '#EAE5E1',
    ACTIVE: '#7A7775',
    COMPLETED: '#FF8000',
} as const

// TODO: JobSeekerSteps.tsx のステップ配列と進捗状態(dot/seg1/seg2)を1つの配列に統合し、しきい値のマジックナンバーを解消する
// しきい値は JobSeekerSteps.tsx のステップ配列の並び順に対応。
// ステップの追加・並び替えを行う場合は両方修正すること。
const getSegmentInfo = (currentStep: number) => {
    let currentDotNumber: number
    let segment1Progress: number
    let segment1Color: string
    let segment2Progress: number
    let segment2Color: string

    // Segment 1 (dot1 → dot2)
    if (currentStep <= 2) {
        // 1〜3ステップ目: 着手中、進捗進む
        segment1Progress = currentStep / 3
        segment1Color = SEGMENT_COLORS.ACTIVE
    } else if (currentStep === 3) {
        // 4ステップ目: currentStep/3 でも progress=1 になるが、色を COMPLETED にせず ACTIVE のまま据え置くために分岐を分けている
        segment1Progress = 1
        segment1Color = SEGMENT_COLORS.ACTIVE
    } else {
        // 5ステップ目以降: 完了
        segment1Progress = 1
        segment1Color = SEGMENT_COLORS.COMPLETED
    }

    // Segment 2 (dot2 → dot3)
    if (currentStep <= 4) {
        // 1〜5ステップ目: 未着手
        segment2Progress = 0
        segment2Color = SEGMENT_COLORS.PENDING
    } else if (currentStep <= 8) {
        // 6〜9ステップ目: 着手中、進捗進む
        segment2Progress = (currentStep - 4) / 5
        segment2Color = SEGMENT_COLORS.ACTIVE
    } else if (currentStep === 9) {
        // 10ステップ目: (currentStep-4)/5 でも progress=1 になるが、色を COMPLETED にせず ACTIVE のまま据え置くために分岐を分けている
        segment2Progress = 1
        segment2Color = SEGMENT_COLORS.ACTIVE
    } else {
        // 11ステップ目以降: 完了
        segment2Progress = 1
        segment2Color = SEGMENT_COLORS.COMPLETED
    }

    // currentDotNumber
    if (currentStep <= 3) {
        currentDotNumber = 1
    } else if (currentStep <= 9) {
        currentDotNumber = 2
    } else if (currentStep === 10) {
        currentDotNumber = 3
    } else {
        currentDotNumber = 4 // 全完了
    }

    return {
        currentDotNumber,
        segment1Progress,
        segment1Color,
        segment2Progress,
        segment2Color,
    }
}

const getDotIcon = (dot: number, currentDotNumber: number): string => {
    const index = dot - 1
    if (dot < currentDotNumber) {
        return JOB_SEEKER_PROGRESS_DOT_IMAGES.COMPLETED_DOT
    }
    if (dot === currentDotNumber) {
        return ACTIVE_ICONS[index]
    }
    return PENDING_ICONS[index]
}

export const JobSeekerProgressBanner = ({ currentStep }: CurrentStepProps) => {
    const {
        currentDotNumber,
        segment1Progress,
        segment1Color,
        segment2Progress,
        segment2Color,
    } = getSegmentInfo(currentStep)

    return (
        <div className={styles.stepperContainer}>
            <div className={styles.headerContainer}>
                <ProgressBannerHeader title="スカウトを受け取ろう" />
                <div className={styles.progressCardWrap}>
                    <div className={styles.progressCard}>
                        <div className={styles.jobSeekerProgressBar}>
                            {/* Segment 1: dot1 → dot2 */}
                            <div className={styles.jobSeekerProgressSegment}>
                                <div
                                    className={
                                        styles.jobSeekerProgressSegmentBg
                                    }
                                />
                                <div
                                    className={
                                        styles.jobSeekerProgressSegmentLine
                                    }
                                    style={{
                                        width: `${segment1Progress * 100}%`,
                                        backgroundColor: segment1Color,
                                    }}
                                />
                            </div>
                            {/* Segment 2: dot2 → dot3 */}
                            <div className={styles.jobSeekerProgressSegment}>
                                <div
                                    className={
                                        styles.jobSeekerProgressSegmentBg
                                    }
                                />
                                <div
                                    className={
                                        styles.jobSeekerProgressSegmentLine
                                    }
                                    style={{
                                        width: `${segment2Progress * 100}%`,
                                        backgroundColor: segment2Color,
                                    }}
                                />
                            </div>
                            <div className={styles.jobSeekerProgressDots}>
                                {Array.from(
                                    { length: TOTAL_DOTS },
                                    (_, i) => i + 1
                                ).map(dot => (
                                    <div
                                        key={dot}
                                        className={styles.jobSeekerDot}
                                    >
                                        <img
                                            src={getDotIcon(
                                                dot,
                                                currentDotNumber
                                            )}
                                            alt={`Step ${dot}`}
                                            className={styles.jobSeekerDotIcon}
                                        />
                                        <span
                                            className={`${styles.jobSeekerDotLabel} ${dot === currentDotNumber ? styles.jobSeekerDotLabelActive : ''}`}
                                        >
                                            {DOT_LABELS[dot - 1]}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
